import { RefObject, useEffect, useRef, useState } from "react";

interface HideOnScrollOptions {
  /** px of movement before we react — keeps tiny nudges from toggling the bar */
  threshold?: number;
  /** always keep the bar shown while within this many px of the top */
  topOffset?: number;
}

/**
 * Tracks scroll direction on `target` (a scroll-container ref, or the window
 * when omitted, since the app uses both) and returns whether a sticky top bar
 * should be hidden. Hides on scroll-down past the top offset, reveals on
 * scroll-up, and always shows near the top. rAF-throttled so it stays cheap
 * during momentum scrolling on mobile.
 */
export const useHideOnScroll = (
  target?: RefObject<HTMLElement | null>,
  { threshold = 8, topOffset = 64 }: HideOnScrollOptions = {}
): boolean => {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const el: HTMLElement | Window = target?.current ?? window;
    const getY = () =>
      el === window ? window.scrollY : (el as HTMLElement).scrollTop;

    lastY.current = getY();

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = getY();
        const delta = y - lastY.current;
        if (y <= topOffset) {
          setHidden(false);
        } else if (Math.abs(delta) > threshold) {
          setHidden(delta > 0); // moving down → hide, up → reveal
        }
        lastY.current = y;
        ticking.current = false;
      });
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [target, threshold, topOffset]);

  return hidden;
};

export default useHideOnScroll;
